import { Mesh, PointerDragBehavior } from 'babylonjs';
import { DragSideConfig } from '@App/app/engine/services/region-of-interest-service/roi-drag-config.service';
import {
  RoiDimensions,
  RoiGeometryService,
} from '@App/app/engine/services/region-of-interest-service/roi-geometry.service';
import { Coordinates3 } from '../layer/measurements/coordinates';

/**
 * Mutable state and collaborators a {@link RegionOfInterest} shares with its drag
 * handlers. `oldPos`/`oldPosMinus` are passed by reference and mutated in place so the
 * owning instance observes the updates; `isShiftPressed` is read live on every drag tick.
 */
export interface RoiDragHandlerContext {
  geometryService: RoiGeometryService;
  sides: Mesh[];
  oldPos: Coordinates3;
  oldPosMinus: Coordinates3;
  size: number;
  isShiftPressed: () => boolean;
  emitDimensions: (dimensions: RoiDimensions) => void;
}

export const registerRoiDragHandlers = (
  ctx: RoiDragHandlerContext,
  behavior: PointerDragBehavior,
  config: DragSideConfig,
  maxSize: number,
): void => {
  const { geometryService, sides, oldPos, oldPosMinus, size } = ctx;
  const { sideIndex, counterIndex, axis, direction } = config;
  const isForward = direction === 'forward';

  behavior.onDragObservable.add(() => {
    if (ctx.isShiftPressed()) {
      geometryService.moveBox(
        sides,
        sideIndex,
        counterIndex,
        axis,
        isForward,
        oldPos,
        oldPosMinus,
        size,
      );
      geometryService.adjustSidesPositions(sides, sideIndex, counterIndex, axis, size);
      oldPos[axis] = sides[isForward ? sideIndex : counterIndex].position[axis];
      oldPosMinus[axis] = sides[isForward ? counterIndex : sideIndex].position[axis];
    } else {
      geometryService.adjustSidesPositions(sides, sideIndex, counterIndex, axis, size);
      if (isForward) {
        oldPos[axis] = sides[sideIndex].position[axis];
      } else {
        oldPosMinus[axis] = sides[sideIndex].position[axis];
      }
      ctx.emitDimensions(geometryService.calculateDimensions(oldPos, oldPosMinus));
    }
  });

  behavior.onDragEndObservable.add(() => {
    if (ctx.isShiftPressed()) {
      geometryService.moveBox(
        sides,
        sideIndex,
        counterIndex,
        axis,
        isForward,
        oldPos,
        oldPosMinus,
        size,
      );
    }
    geometryService.clampSideToMaxSize(sides, sideIndex, counterIndex, axis, maxSize, isForward);
    geometryService.adjustSidesPositions(sides, sideIndex, counterIndex, axis, size);
    oldPos[axis] = sides[isForward ? sideIndex : counterIndex].position[axis];
    oldPosMinus[axis] = sides[isForward ? counterIndex : sideIndex].position[axis];
    ctx.emitDimensions(geometryService.calculateDimensions(oldPos, oldPosMinus));
  });
};
